import React, { useState, useEffect } from "react";
import Select from "react-select";
import "./Form.css";
import {
  getProvider,
  registerAutoSwitchProvider,
} from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/common/ConfirmModal";
import { Close } from "@mui/icons-material";

function FormRegisterProvider({ listofusers }) {
  const token = useSelector((store) => store.userReducer.token);

  const [formData, setFormData] = useState({
    userId: "",
    successRate: "",
    mode: [],
    switchData: {},
  });

  const [hoveredId, setHoveredId] = useState(null);
  const [totalOptions, setTotalOptions] = useState([]);
  const [draggedId, setDraggedId] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedMode, setSelectedMode] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [providerData, setProviderData] = useState([]);
  const [modeWiseProviders, setModeWiseProviders] = useState({});

  useEffect(() => {
    const fetchProviderData = async () => {
      try {
        const response = await getProvider(token);
        const data = response?.data?.providers || [];
        setProviderData(data);
      } catch (error) {
        console.error("Error fetching provider data:", error.message);
      }
    };
    fetchProviderData();
  }, [token]);

  useEffect(() => {
    const userOptions = listofusers?.map((user) => ({
      value: user.id,
      label: user?.alias || user?.id,
    }));
    setTotalOptions(userOptions);
  }, [listofusers]);

  const uniqueModes = [
    ...new Set((providerData || []).map((item) => item.mode)),
  ].filter(Boolean);

  const modeOptions = uniqueModes.map((mode) => ({
    value: mode,
    label: mode,
  }));

  const handleInputChange = (field, value) => {
    if (field === "userId") {
      setSelectedMerchant(value);
      setFormData((prev) => ({
        ...prev,
        userId: value.value,
      }));
    } else if (field === "mode") {
      const selectedModes = value.map((val) => val.value);
      const updatedSwitchData = {};
      selectedModes.forEach((mode) => {
        updatedSwitchData[mode] = formData.switchData[mode] || [];
      });

      setSelectedMode(value);
      setFormData((prev) => ({
        ...prev,
        mode: selectedModes,
        switchData: updatedSwitchData,
      }));

      const groupedProviders = {};
      selectedModes.forEach((mode) => {
        groupedProviders[mode] = providerData
          .filter((p) => p.mode === mode)
          .map((p) => ({
            label: `${p.providerName} - ${p.orgName}`,
            value: p.id,
          }));
      });
      setModeWiseProviders(groupedProviders);
    } else if (uniqueModes.includes(field)) {
      const selectedOptions = value || [];
      setFormData((prev) => ({
        ...prev,
        switchData: {
          ...prev.switchData,
          [field]: selectedOptions.map((opt) => opt.value),
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userConfirmed = await openConfirmation();
    if (!userConfirmed) return;

    setLoading(true);

    const payload = {
      ...formData,
      successRate: 100,
    };

    try {
      await registerAutoSwitchProvider(token, payload);
      toast.success("Switch data submitted successfully.");
    } catch (err) {
      toast.error("Submission failed.");
      console.error(err.message);
    } finally {
      setLoading(false);
      setSelectedMerchant(null);
      setSelectedMode([]);
      setFormData({
        userId: "",
        successRate: "",
        mode: [],
        switchData: {},
      });
      setModeWiseProviders({});
    }
  };

  const openConfirmation = () => {
    return new Promise((resolve) => {
      setIsConfirmModal(() => ({
        isOpen: true,
        onResolve: resolve,
      }));
    });
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
  };

  const handleDragEnter = (e, targetId, mode) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetId) return;

    setFormData((prev) => {
      const list = [...(prev.switchData[mode] || [])];
      const draggedIndex = list.indexOf(draggedId);
      const targetIndex = list.indexOf(targetId);
      if (draggedIndex === -1 || targetIndex === -1) return prev;
      // Move dragged item to hovered position immediately
      list.splice(draggedIndex, 1);
      list.splice(targetIndex, 0, draggedId);
      return {
        ...prev,
        switchData: {
          ...prev.switchData,
          [mode]: list,
        },
      };
    });

    setHoveredId(targetId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedId(null);
    setHoveredId(null);
  };

  const handleRemoveProvider = (e, idToRemove, mode) => {
    e.stopPropagation();

    setFormData((prev) => ({
      ...prev,
      switchData: {
        ...prev.switchData,
        [mode]: (prev.switchData[mode] || []).filter((id) => id !== idToRemove),
      },
    }));
  };

  return (
    <div className="switch-form-wallet-container">
      <h2 className="form-title">Auto Switch Provider Register</h2>
      <form className="wallet-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Select Merchant</label>
          <Select
            options={totalOptions}
            value={selectedMerchant}
            onChange={(e) => handleInputChange("userId", e)}
            placeholder="Select Merchant"
            className="form-select"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Select Mode</label>
          <Select
            options={modeOptions}
            value={selectedMode}
            isMulti
            onChange={(e) => handleInputChange("mode", e)}
            placeholder="Select Mode(s)"
            className="form-select"
            required
          />
        </div>

        {formData.mode.map((mode) => (
          <div key={mode} className="form-group">
            <label className="form-label">Select {mode} Provider</label>
            <Select
              options={modeWiseProviders[mode] || []}
              isMulti
              onChange={(selected) => handleInputChange(mode, selected)}
              placeholder={`Select ${mode} providers`}
              className="form-select"
              value={(formData.switchData[mode] || [])
                .map((id) =>
                  (modeWiseProviders[mode] || []).find(
                    (opt) => opt.value === id
                  )
                )
                .filter(Boolean)}
              controlShouldRenderValue={false}
            />

            {formData.switchData[mode].length ? <div className="selected-options">
              {(formData.switchData[mode] || []).map((id) => {
                const option = (modeWiseProviders[mode] || []).find(
                  (opt) => opt.value === id
                );
                if (!option) return null;
                return (
                  <div
                    className={`option-chip ${
                      draggedId === id ? "dragging" : ""
                    }`}
                    key={id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, id)}
                    onDragEnter={(e) => handleDragEnter(e, id, mode)}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <p className="selected-option-chip">{option.label}</p>
                    <Close onClick={(e) => handleRemoveProvider(e, id, mode)} />
                  </div>
                );
              })}
            </div> : ''}
          </div>
        ))}
        {/* 
        <div className="form-group">
          <label className="form-label">Success Rate (%)</label>
          <input
            type="number"
            placeholder="Enter success rate"
            className="form-input"
            value={formData.successRate}
            onChange={(e) =>
              handleInputChange("successRate", e.target.value)
            }
            min="1"
            max="100"
            required
          />
        </div> */}

        <div className="form-group">
          <button
            type="submit"
            className="fund-load-submit-button"
            disabled={loading}
          >
            {loading ? <div className="fund-load-loader"></div> : "Submit"}
          </button>
        </div>
      </form>

      {isConfirmModal && (
        <ConfirmationModal
          onResolve={(decision) => {
            isConfirmModal.onResolve(decision);
            setIsConfirmModal(false);
          }}
        />
      )}
    </div>
  );
}

export default FormRegisterProvider;
